/**
 * Listen to Abby's voice for one language, with the paragraph she is reading
 * highlighted (spec 2026-10-23 §5 Tab 2: "the same reveal the member sees").
 *
 * The rule is lib/welcome-sync.ts: paragraph i while startMs(i) ≤ t <
 * startMs(i+1), the last one until timing.totalDurationMs. The length shown
 * and used is ALWAYS timing.totalDurationMs: the joined file has no Xing
 * header and mixes bit rates, so the player's own duration is wrong. That is
 * why this player has its own controls and never shows the browser's.
 *
 * Accessibility: Play/Pause is a button named with the language; the position
 * is a labelled slider that says "0:12 of 1:22"; the highlighted paragraph is
 * marked by a thick bar, bold text and the words "Now reading" — never by
 * color alone (WCAG 1.4.1) — and by aria-current. With "reduce motion", the
 * list jumps to the paragraph instead of scrolling smoothly.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Pause, Play, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WelcomeVoiceLocale } from '../lib/contract';
import { activeParagraph, formatClock, positionOf } from '../lib/welcome-sync';

interface Props {
  voice: WelcomeVoiceLocale;
  languageName: string;
  /** The voice is for text that has changed since: say so. */
  stale: boolean;
  /** Start of the element ids. */
  idPrefix: string;
}

function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function SyncedPreview({ voice, languageName, stale, idPrefix }: Props) {
  const total = voice.timing.totalDurationMs;
  const audio = useRef<HTMLAudioElement>(null);
  const list = useRef<HTMLOListElement>(null);
  const items = useRef<(HTMLLIElement | null)[]>([]);
  const frame = useRef<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [position, setPosition] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const ids = `${idPrefix}-${languageName.toLowerCase()}-preview`;

  // Nothing is highlighted before the first play: the member's card also starts empty.
  const active = started ? activeParagraph(voice.timing, position) : -1;

  /** Read the player's position; stop at the REAL end (totalDurationMs). */
  const sample = useCallback(() => {
    const el = audio.current;
    if (!el) return;
    const ms = positionOf(el.currentTime, total);
    setPosition(ms);
    if (ms >= total && !el.paused) el.pause();
  }, [total]);

  // While playing, follow the position every frame (timeupdate fires only ~4 times a second).
  useEffect(() => {
    if (!playing) return;
    const tick = () => {
      sample();
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
    };
  }, [playing, sample]);

  // Keep the paragraph being read in view, inside the list only (the page does not move).
  useEffect(() => {
    if (!playing || active < 0) return;
    const box = list.current;
    const item = items.current[active];
    if (!box || !item) return;
    const top = item.offsetTop - box.clientHeight / 3;
    box.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }, [active, playing]);

  const toggle = () => {
    const el = audio.current;
    if (!el) return;
    if (!el.paused) {
      el.pause();
      return;
    }
    if (positionOf(el.currentTime, total) >= total) el.currentTime = 0;
    setStarted(true);
    void el.play().catch(() => setLoadError(true));
  };

  const seek = (ms: number) => {
    const el = audio.current;
    if (!el) return;
    el.currentTime = ms / 1000;
    setStarted(true);
    setPosition(ms);
  };

  const clock = `${formatClock(position)} of ${formatClock(total)}`;

  return (
    <section aria-labelledby={`${ids}-heading`} className="space-y-3 rounded-lg border bg-card p-4">
      <h5 id={`${ids}-heading`} className="text-sm font-semibold">
        Listen: {languageName}
      </h5>
      {stale && (
        <p className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          This voice is for the text before your last change. Generate Abby&apos;s voice again before you publish.
        </p>
      )}

      <audio
        ref={audio}
        src={voice.audio_url}
        preload="auto"
        onPlay={() => setPlaying(true)}
        onPause={() => {
          setPlaying(false);
          sample();
        }}
        onEnded={() => {
          setPlaying(false);
          setPosition(total);
        }}
        onSeeked={sample}
        onError={() => setLoadError(true)}
      />

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          disabled={loadError}
          aria-label={`${playing ? 'Pause' : 'Play'} the ${languageName} voice`}
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {playing ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
          {playing ? 'Pause' : 'Play'}
        </button>
        <input
          type="range"
          min={0}
          max={total}
          step={100}
          value={Math.min(position, total)}
          onChange={(e) => seek(Number(e.target.value))}
          disabled={loadError}
          aria-label={`Position in the ${languageName} voice`}
          aria-valuetext={clock}
          className="h-2 min-w-0 flex-1 cursor-pointer accent-primary"
        />
        <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground" aria-hidden="true">
          {formatClock(position)} / {formatClock(total)}
        </span>
      </div>

      {loadError && (
        <p role="alert" className="flex gap-2 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          The {languageName} voice could not be played. Check the internet connection, then reload the page.
        </p>
      )}

      <ol ref={list} className="relative max-h-80 space-y-2 overflow-y-auto pr-1" aria-label={`${languageName} paragraphs, as Abby reads them`}>
        {voice.paragraphs.map((p, i) => {
          const now = i === active;
          return (
            <li
              key={i}
              ref={(el) => {
                items.current[i] = el;
              }}
              aria-current={now ? 'true' : undefined}
              className={cn(
                'rounded-r-md py-1.5 pl-3 pr-2 text-sm',
                now ? 'border-l-[6px] border-primary bg-primary/10 font-semibold' : 'border-l-2 border-border text-muted-foreground',
              )}
            >
              {now && <span className="mb-0.5 block text-xs font-bold uppercase tracking-wide">Now reading</span>}
              {p}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
